import { Transaction, Session, TransactionEvent, OrderItem, ShippingOption, SessionItem, PayoutSplit } from '../../types/checkout';

import { TransactionItem, CaptureState, RefundState } from '../types';
import { deduplicateSplits, getSkewedSplits, getOrderItemsSplits, subtractRefundedFromCapturedSplits } from '../splitPaymentHelpers';

export const getShippingOptionItem = (shippingOption: ShippingOption): OrderItem => {
    const shippingOptionItem: OrderItem = {
        id: shippingOption.id,
        line_id: shippingOption.line_id,
        amount: shippingOption.amount,
        description: [shippingOption.operator, shippingOption.title].join(', '),
        splits: shippingOption.splits,
        fee_split: shippingOption.fee_split,
        quantity: 1,
    };
    if (shippingOption.vat) {
        shippingOptionItem.vat = shippingOption.vat;
    }
    if (shippingOption.vat_amount) {
        shippingOptionItem.vat_amount = shippingOption.vat_amount;
    }
    return shippingOptionItem;
};

export const getItems = (session: Session): SessionItem[] => {
    // The shipping option needs to be added to the items list, since the
    // "shipping option item" will only appear on transactions and not in
    // the session item list.
    // If the items list is empty we do not add the sipping option item.
    const sessionItems = session.order.items || [];
    if (sessionItems.length > 0 && session.order.shipping_option) {
        return [...sessionItems, getShippingOptionItem(session.order.shipping_option)];
    }
    return sessionItems;
};

const getCapturableSplits = (item: SessionItem, capturableAmount: number) => {
    const ratio = capturableAmount / (item.amount || 0);
    if (ratio === 0 || isNaN(ratio)) {
        return [];
    }
    if (ratio === 1) {
        return item.splits || [];
    }
    return getSkewedSplits(item.splits || [], ratio, capturableAmount);
};

const filterItemSuccessEvents = (events: TransactionEvent[], eventType: TransactionEvent['event'], lineId: string) => {
    return events.filter(event => {
        if (event.event === eventType && event.success) {
            return (event.items || []).find(eventItem => eventItem.line_id === lineId);
        }
        return false;
    });
};

const getRefundableSplits = (item: SessionItem, refundableAmount: number, significantEvents: TransactionEvent[]) => {
    const ratio = refundableAmount / (item.amount || 0);
    if (ratio === 0 || isNaN(ratio)) {
        return [];
    }
    const captureItemEvents = filterItemSuccessEvents(significantEvents, 'CAPTURE', item.line_id || '');
    const captureSplits = deduplicateSplits(
        captureItemEvents.map(event => getOrderItemsSplits(event.items || [], item.line_id || '')).flat()
    );

    const refundItemEvents = filterItemSuccessEvents(significantEvents, 'REFUND', item.line_id || '');
    const refundSplits = deduplicateSplits(
        refundItemEvents.map(event => getOrderItemsSplits(event.items || [], item.line_id || '')).flat()
    );

    return subtractRefundedFromCapturedSplits(captureSplits, refundSplits);
};


export const getTransactionItems = (transaction: Transaction, session: Session): TransactionItem[] => {
    const sessionItems = getItems(session);
    const transactionEvents = transaction.events || [];

    const result: TransactionItem[] = sessionItems.map((item) => {
        const significantEvents = getSignificantEvents(item, transactionEvents);
        const capturableAmount = getCapturableAmount(item, significantEvents);
        const capturableQuantity = getCapturableQuantity(item, significantEvents);
        const captureState = getCaptureState(item, capturableAmount);
        const refundableAmount = getRefundableAmount(item, significantEvents);
        const refundableQuantity = getRefundableQuantity(item, significantEvents);
        const refundState = getRefundState(item, significantEvents);
        const capturableSplits = getCapturableSplits(item, capturableAmount);
        const refundableSplits = getRefundableSplits(item, refundableAmount, significantEvents);

        return {
            orderItem: item,
            events: significantEvents,
            capturableAmount,
            capturableQuantity,
            captureState,
            refundableAmount,
            refundableQuantity,
            refundState,
            capturableSplits,
            refundableSplits,
        };
    });
    return result;
};

const getSignificantEvents = (item: OrderItem, events: TransactionEvent[]) => {
    return events.filter((event) => {
        return event.items && event.items.find((eventItem) => eventItem.line_id === item.line_id) && true;
    });
};

const getCapturableAmount = (item: OrderItem, significantEvents: TransactionEvent[]) => {
    // events are all significant to this item
    return significantEvents.reduce((capturable: number, event) => {
        if (event.event === 'CAPTURE' && event.success) {
            const eventItems = event.items || [];
            const capturedAmount = eventItems.reduce((captured, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventAmount = eventItem.amount || 0;
                    return captured + eventAmount;
                }
                return captured;
            }, 0);
            return capturable - capturedAmount;
        }
        return capturable;
    }, item.amount || 0);
};

const getCapturableQuantity = (item: OrderItem, significantEvents: TransactionEvent[]) => {
    return significantEvents.reduce((capturable: number, event) => {
        if (event.event === 'CAPTURE' && event.success) {
            const eventItems = event.items || [];
            const capturedQuantity = eventItems.reduce((captured, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventQuantity = eventItem.quantity || 0;
                    return captured + eventQuantity;
                }
                return captured;
            }, 0);
            return capturable - capturedQuantity;
        }
        return capturable;
    }, item.quantity || 0);
};

const getCaptureState = (item: OrderItem, capturableAmount: number) => {
    if (capturableAmount === item.amount) {
        return CaptureState.Authorized;
    }
    if (capturableAmount === 0) {
        return CaptureState.Captured;
    }
    return CaptureState.PartiallyCaptured;
};

const getRefundableAmount = (item: OrderItem, significantEvents: TransactionEvent[]) => {
    return significantEvents.reduce((refundable: number, event) => {
        const eventItems = event.items || [];
        if (event.event === 'CAPTURE' && event.success) {
            const capturedAmount = eventItems.reduce((captured, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventAmount = eventItem.amount || 0;
                    return captured + eventAmount;
                }
                return captured;
            }, 0);
            return refundable + capturedAmount;
        } else if (event.event === 'REFUND' && event.success) {
            const refundedAmount = eventItems.reduce((refunded, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventAmount = eventItem.amount || 0;
                    return refunded + eventAmount;
                }
                return refunded;
            }, 0);
            return refundable - refundedAmount;
        }
        return refundable;
    }, 0);
};

const getRefundableQuantity = (item: OrderItem, significantEvents: TransactionEvent[]) => {
    return significantEvents.reduce((refundable: number, event) => {
        const instabankItems = event.items || [];
        if (event.event === 'CAPTURE' && event.success) {
            const capturedQuantity = instabankItems.reduce((captured, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventQuantity = eventItem.quantity || 0;
                    return captured + eventQuantity;
                }
                return captured;
            }, 0);
            return refundable + capturedQuantity;
        } else if (event.event === 'REFUND' && event.success) {
            const refundedQuantity = instabankItems.reduce((refunded, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventQuantity = eventItem.quantity || 0;
                    return refunded + eventQuantity;
                }
                return refunded;
            }, 0);
            return refundable - refundedQuantity;
        }
        return refundable;
    }, 0);
};

const getRefundState = (item: OrderItem, significantEvents: TransactionEvent[]) => {
    let isPristine = true;

    const refundableAmount = significantEvents.reduce((refundable: number, event) => {
        const eventItems = event.items || [];
        if (event.event === 'CAPTURE' && event.success) {
            const capturedAmount = eventItems.reduce((captured, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    const eventAmount = eventItem.amount || 0;
                    return captured + eventAmount;
                }
                return captured;
            }, 0);
            return refundable + capturedAmount;
        } else if (event.event === 'REFUND' && event.success) {
            const refundedAmount = eventItems.reduce((refunded, eventItem) => {
                if (eventItem.line_id === item.line_id) {
                    isPristine = false;
                    const eventAmount = eventItem.amount || 0;
                    return refunded + eventAmount;
                }
                return refunded;
            }, 0);
            return refundable - refundedAmount;
        }
        return refundable;
    }, 0);

    if (isPristine) {
        return RefundState.Pristine;
    } else if (refundableAmount === 0) {
        return RefundState.Refunded;
    }
    return RefundState.PartiallyRefunded;
};

export const hasCapturableState = (transaction: Transaction): boolean => {
    return ['AUTHORIZED', 'PARTIALLY_CAPTURED', 'PARTIALLY_REFUNDED', 'PARTIALLY_CAPTURED_REFUNDED'].includes(
        transaction.status || ''
    );
};
